import React, {memo, useCallback, useMemo, useState} from 'react';
import Button from 'components/ui/Button';
import {SingleSelect} from 'components/ui/MultiSelect';
import DropdownMenu, {
	DropdownMenuContent,
	DropdownMenuItem,
} from 'components/ui/DropdownMenu';
import {ChevronDown, Redo, Check} from 'lucide-react';
import {useIntl} from 'contexts/IntlContext';
import {useLists} from 'data/list';
import {useNextListItem} from 'data/listItem';
import useOverlayDockStore from 'stores/overlayDockStore';
import {OVERLAY_TOOL_ID_NEXT_LIST_ITEM} from '../tools';
import ListItemDetailsModal from 'views/Lists/components/ListItemDetailsModal';
import {updateListItem} from 'modules/lists/io';
import useNotifications from 'hooks/useNotifications';
import {useAuth} from 'hooks/useAuth';
import ListItemCard from './ListItemCard';
import {useQueryClient} from '@tanstack/react-query';
import {
	addDays,
	addHours,
	addMinutes,
	formatISO,
	nextMonday,
	set,
	startOfDay,
} from 'date-fns';

const LIST_ITEM_INCLUDES = [
	'comments',
	'comment',
	// buildings
	'listable.tags',
	'listable.clients',
	'listable.buildings',
	'listable.allBuildingProductStates.product',
	'listable.building.allBuildingProductStates.product',

	// leads
	'listable.client',
	'listable.user',
	'listable.salesmanager.organizations',
	'listable.calendarResource.team',
	'listable.products',
	'listable.organization',
	'listable.leadEvents.user',
	'listable.meta',
];

const createBlockedUntilIso = kind => {
	const now = new Date();
	let until;
	switch (kind) {
		case '15m':
			until = addMinutes(now, 15);
			break;
		case '30m':
			until = addMinutes(now, 30);
			break;
		case '1h':
			until = addHours(now, 1);
			break;
		case '2h':
			until = addHours(now, 2);
			break;
		case 'nextMorning': {
			const tomorrow = addDays(startOfDay(now), 1);
			until = set(tomorrow, {hours: 8, minutes: 0, seconds: 0, milliseconds: 0});
			break;
		}
		case 'nextWeek': {
			const monday = nextMonday(now);
			until = set(startOfDay(monday), {
				hours: 8,
				minutes: 0,
				seconds: 0,
				milliseconds: 0,
			});
			break;
		}
		case '1d':
		default:
			until = addDays(now, 1);
	}
	return formatISO(until);
};

const NextListItemTool = memo(() => {
	const intl = useIntl();
	const {user} = useAuth();
	const {openNotification} = useNotifications();
	const [detailsItem, setDetailsItem] = useState(null);
	const queryClient = useQueryClient();

	const listId = useOverlayDockStore(
		s => s.toolStateById?.[OVERLAY_TOOL_ID_NEXT_LIST_ITEM]?.listId || null,
	);
	const skipKind = useOverlayDockStore(
		s => s.toolStateById?.[OVERLAY_TOOL_ID_NEXT_LIST_ITEM]?.skipKind || 'nextMorning',
	);
	const setToolState = useOverlayDockStore(s => s.setToolState);

	const handleListIdChange = useCallback(
		next => {
			setToolState(OVERLAY_TOOL_ID_NEXT_LIST_ITEM, {listId: next || null});
		},
		[setToolState],
	);

	const handleSkipKindChange = useCallback(
		next => {
			setToolState(OVERLAY_TOOL_ID_NEXT_LIST_ITEM, {skipKind: next || 'nextMorning'});
		},
		[setToolState],
	);

	const {data: listsResponse, isLoading: isListsLoading} = useLists();

	// Support both unwrap patterns:
	// - custom unwrap: `{data: [...]}` (resource unwrap: x => x)
	// - default unwrap: `[...]`
	const lists = useMemo(() => {
		if (Array.isArray(listsResponse?.data)) return listsResponse.data;
		if (Array.isArray(listsResponse)) return listsResponse;
		return [];
	}, [listsResponse]);

	const listOptions = useMemo(
		() =>
			lists.map(l => ({
				value: l.id,
				label: l.title || l.name || `#${l.id}`,
			})),
		[lists],
	);

	const nextListItemParams = useMemo(
		() => ({
			listId,
			// Similar to: views/Lists/components/ListView.js:197, Extract on 3rd occurrence
			include: LIST_ITEM_INCLUDES,
		}),
		[listId],
	);

	const {
		data: nextListItemResponse,
		error,
		isFetching,
		refetch,
	} = useNextListItem({
		params: nextListItemParams,
		useQueryOptions: {
			enabled: !!listId,
			retry: (failureCount, err) => {
				// Backend uses 404 to indicate "no more items"
				if (err?.status === 404) return false;
				return failureCount < 1;
			},
		},
	});

	const nextItem = nextListItemResponse?.data ?? nextListItemResponse ?? null;
	const noMoreItems = error?.status === 404;

	const handleNext = useCallback(() => {
		if (!listId) return;
		refetch();
	}, [listId, refetch]);

	/**
	 * Keep legacy list views in sync.
	 *
	 * Lists view (`views/Lists/components/ListView.js`) uses react-query keys starting with 'listItems'
	 * and does NOT use `data/listItem` yet. When we mutate a list item here we should invalidate those
	 * queries so open list views refresh, without invalidating `nextListItem`.
	 */
	const invalidateLegacyListItemsQueries = useCallback(() => {
		queryClient.invalidateQueries({
			predicate: query =>
				Array.isArray(query.queryKey) &&
				query.queryKey[0] === 'listItems' &&
				(listId ? query.queryKey.includes(listId) : true),
		});
	}, [queryClient, listId]);

	const setCachedNextItemPatch = useCallback(
		patch => {
			const queryKey = useNextListItem.getQueryKey(nextListItemParams);
			queryClient.setQueryData(queryKey, current => {
				const currentItem = current?.data ?? current ?? null;
				if (!currentItem) return current;
				const nextPatched = {...currentItem, ...patch};
				return current?.data !== undefined
					? {...current, data: nextPatched}
					: nextPatched;
			});
		},
		[nextListItemParams, queryClient],
	);

	const handleProcessItem = useCallback(
		async item => {
			try {
				const body = {
					processedAt: !item.processedAt ? new Date().toISOString() : null,
					processedBy: !item.processedBy ? user?.id : null,
				};
				const res = await updateListItem(item.id, body);
				setCachedNextItemPatch(
					res?.data
						? {processedAt: res.data.processedAt, processedBy: res.data.processedBy}
						: body,
				);
				invalidateLegacyListItemsQueries();
				return res;
			} catch {
				openNotification({
					type: 'error',
					message: intl.formatMessage({id: 'Unexpected error'}),
					duration: 3000,
				});
			}
		},
		[
			user?.id,
			openNotification,
			intl,
			setCachedNextItemPatch,
			invalidateLegacyListItemsQueries,
		],
	);

	const skipOptions = useMemo(
		() => [
			{value: '15m', labelId: '15 min'},
			{value: '30m', labelId: '30 min'},
			{value: '1h', labelId: '1 hour'},
			{value: '2h', labelId: '2 hours'},
			{value: 'nextMorning', labelId: 'Until next morning'},
			{value: '1d', labelId: '1 day'},
			{value: 'nextWeek', labelId: 'Until next week'},
		],
		[],
	);

	const skipButtonLabelId = useMemo(() => {
		switch (skipKind) {
			case '15m':
				return 'Skip for 15 min';
			case '30m':
				return 'Skip for 30 min';
			case '1h':
				return 'Skip for 1 hour';
			case '2h':
				return 'Skip for 2 hours';
			case '1d':
				return 'Skip for 1 day';
			case 'nextWeek':
				return 'Skip until next week';
			case 'nextMorning':
			default:
				return 'Skip until tomorrow';
		}
	}, [skipKind]);

	const doSkipAndNext = useCallback(
		async kind => {
			if (!nextItem) return;
			const blockedUntil = createBlockedUntilIso(kind);
			try {
				const res = await updateListItem(nextItem.id, {blockedUntil});
				setCachedNextItemPatch(
					res?.data?.blockedUntil
						? {blockedUntil: res.data.blockedUntil}
						: {blockedUntil},
				);
				invalidateLegacyListItemsQueries();
				await refetch();
				return res;
			} catch {
				openNotification({
					type: 'error',
					message: intl.formatMessage({id: 'Unexpected error'}),
					duration: 3000,
				});
			}
		},
		[
			nextItem,
			openNotification,
			intl,
			refetch,
			setCachedNextItemPatch,
			invalidateLegacyListItemsQueries,
		],
	);

	const doResolveAndNext = useCallback(async () => {
		if (!nextItem) return;
		try {
			const processedAt = new Date().toISOString();
			const processedBy = user?.id || null;
			const res = await updateListItem(nextItem.id, {processedAt, processedBy});
			setCachedNextItemPatch(
				res?.data
					? {processedAt: res.data.processedAt, processedBy: res.data.processedBy}
					: {processedAt, processedBy},
			);
			invalidateLegacyListItemsQueries();
			await refetch();
			return res;
		} catch {
			openNotification({
				type: 'error',
				message: intl.formatMessage({id: 'Unexpected error'}),
				duration: 3000,
			});
		}
	}, [
		nextItem,
		user?.id,
		openNotification,
		intl,
		refetch,
		setCachedNextItemPatch,
		invalidateLegacyListItemsQueries,
	]);

	const skipSplitButton = useMemo(() => {
		const durationTrigger = (
			<Button
				variant="ghost"
				size="sm"
				className="rounded-l-none px-2"
				aria-label={intl.formatMessage({id: 'Select skip duration'})}
			>
				<ChevronDown className="h-4 w-4" />
			</Button>
		);

		return (
			<div className="flex items-center rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
				<Button
					variant="ghost"
					size="sm"
					className="rounded-r-none"
					onClick={() => doSkipAndNext(skipKind)}
					disabled={!nextItem || isFetching}
				>
					{intl.formatMessage({id: skipButtonLabelId})}
				</Button>
				<DropdownMenu trigger={durationTrigger} placement="top-end">
					{close => (
						<DropdownMenuContent>
							{skipOptions.map(opt => (
								<DropdownMenuItem
									key={opt.value}
									selected={skipKind === opt.value}
									onClick={() => {
										handleSkipKindChange(opt.value);
										close?.();
									}}
								>
									{intl.formatMessage({id: opt.labelId})}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					)}
				</DropdownMenu>
			</div>
		);
	}, [
		doSkipAndNext,
		handleSkipKindChange,
		intl,
		isFetching,
		nextItem,
		skipButtonLabelId,
		skipKind,
		skipOptions,
	]);

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-col gap-1">
				<div className="flex items-center justify-start gap-2">
					<span className="text-sm text-gray-500 dark:text-gray-300">
						{intl.formatMessage({id: 'List'})}:
					</span>
					<SingleSelect
						value={listId}
						onChange={handleListIdChange}
						options={listOptions}
						placeholder={intl.formatMessage({id: 'Select a list'})}
						clearable={false}
						disabled={isListsLoading}
						size="sm"
						className="flex-1 min-w-0"
					/>
				</div>
			</div>

			{/* Result */}
			<div className="">
				{!listId ? (
					<div className="text-sm text-gray-500 dark:text-gray-300">
						{intl.formatMessage({id: 'Select a list'})}
					</div>
				) : isListsLoading ? (
					<div className="text-sm text-gray-500 dark:text-gray-300">
						{intl.formatMessage({id: 'Loading...'})}
					</div>
				) : noMoreItems ? (
					<div className="text-sm text-gray-500 dark:text-gray-300">
						{intl.formatMessage({id: 'No more items available'})}
					</div>
				) : error ? (
					<div className="text-sm text-gray-500 dark:text-gray-300">
						{intl.formatMessage(
							{id: 'Failed to load item'},
							{status: error?.status || ''},
						)}
					</div>
				) : nextItem ? (
					<div className="flex flex-col gap-3">
						<ListItemCard
							listItem={nextItem}
							intl={intl}
							onProcessItem={handleProcessItem}
							onShowDetails={() => setDetailsItem(nextItem)}
						/>

						{!nextItem.processedAt ? (
							<div className="flex items-center justify-end gap-2">
								{skipSplitButton}

								<Button
									variant="green"
									size="sm"
									title={intl.formatMessage({id: 'Resolve + Next'})}
									onClick={doResolveAndNext}
								>
									<Check className="size-4" />+<Redo className="size-4" />
								</Button>
							</div>
						) : (
							<div className="flex items-center justify-end gap-2">
								<Button
									variant="green"
									size="sm"
									onClick={handleNext}
									disabled={isFetching || !listId}
								>
									{intl.formatMessage({id: 'Next'})}
								</Button>
							</div>
						)}
					</div>
				) : (
					<div className="text-sm text-gray-600 dark:text-gray-300">
						{intl.formatMessage({id: 'Press Next to load an item'})}
					</div>
				)}
			</div>

			{detailsItem && (
				<ListItemDetailsModal
					listItem={detailsItem}
					onClose={() => setDetailsItem(null)}
					processCallback={() => refetch()}
				/>
			)}
		</div>
	);
});

NextListItemTool.displayName = 'NextListItemTool';

export default NextListItemTool;
